import CompanyDocumentOverview from "features/company-documents/components/CompanyDocumentOverview";
import DocumentReviewModal from "features/company-documents/components/DocumentReviewModal";
import useDocumentAccess from "features/company-documents/useDocumentAccess";
import { Button, Tooltip } from "@mui/material";
import {
  CustomTable,
  FilterGroupComponent,
  OverflowText,
  SnackbarComponent,
} from "components";
import { useCallback, useEffect, useMemo, useState } from "react";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CreateEditAccountModal from "./components/CreateEditAccountModal";
import { emptyToastMessage } from "consts/common";
import storage from "services/storage";
import "./ClientManagementPage.css";
import { capitalize } from "lodash";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetCompanyDetails } from "services/company";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import CreateEditClientModal from "./components/CreateEditClientModal";
import { useSnackbar } from "components/snackbar/SnackbarContext";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DetailAccountModal from "./components/DetailAccountModal";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import DeleteAccountModal from "./components/DeleteAccountModal";
import { SHOWN_USER_ROLE } from "consts/UserManagement";
import { useGetAccountList } from "./service/ClientManagementService";
import { paramParsify } from "utils/queryParams";
import CreateEditRecipientModal from "./components/CreateEditRecipientModal";
import DeleteRecipientModal from "./components/DeleteRecipientModal";

const ClientManagementDetailPage = ({ setLayoutTitle }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const documentAccess = useDocumentAccess(storage.getItem("type") === "ADMIN", id);
  const [selectedDocument,setSelectedDocument] = useState(null);
  const location = useLocation();
  const queryParams = paramParsify(location?.search);
  const { message, setMessage } = useSnackbar();
  const isAdminView = useMemo(
    () => storage.getItem("type") === "ADMIN" && ["ADMIN", "SUPER ADMIN"].includes(storage.getItem("role")),
    [],
  );

  const [createEditAccountModal, setCreateEditAccountModal] = useState({
    open: false,
  });
  const [createEditClientModal, setCreateEditClientModal] = useState({
    open: false,
  });
  const [createEditRecipientModal, setCreateEditRecipientModal] = useState({
    open: false,
  });
  const [detailAccountModal, setDetailAccountModal] = useState({
    open: false,
  });
  const [deleteAccountModal, setDeleteAccountModal] = useState({
    open: false,
  });
  const [deleteRecipientModal, setDeleteRecipientModal] = useState({
    open: false,
  });

  const { response: companyDetail, refetch: refetchCompanyDetail } =
    useGetCompanyDetails({ id });
  const { response: accountList, refetch: refetchAccountList } =
    useGetAccountList({ id });
  const {
    response: accountListWithoutLimit,
    refetch: refetchAccountListWithoutLimit,
  } = useGetAccountList({
    id,
    skip_pagination: true,
  });

  const showCreateAccountBtn = useMemo(() => {
    const userLimit = companyDetail?.data?.subscription_type === "FREE" ? 2 : 5;
    const activeUsers = accountListWithoutLimit?.data?.company_users?.filter(
      (el) => el?.status === "ACTIVE",
    );
    return (activeUsers?.length ?? 0) < userLimit;
  }, [companyDetail, accountListWithoutLimit]);

  const showCreateRecipientBtn = useMemo(() => {
    const limit = 10;
    return companyDetail?.data?.email_recipients?.length < limit;
  }, [companyDetail]);

  const headCells = [
    {
      id: "name",
      label: <p className='text-xs'>Account Name</p>,
    },
    {
      id: "email",
      label: <p className='text-xs'>Account E-mail</p>,
    },
    {
      id: "role",
      label: <p className='text-xs'>Role</p>,
    },
    {
      id: "status",
      label: <p className='text-xs'>Account Status</p>,
    },
    {
      id: "actions",
      label: <p className='text-xs'>Actions</p>,
    },
  ];

  const handleOpenCreateEditAccountModal = (id) => {
    setCreateEditAccountModal({ open: true, id });
  };
  const handleOpenCreateEditClientModal = (id) => {
    setCreateEditClientModal({ open: true, id });
  };

  const handleOpenDeleteAccountModal = (id) => {
    setDeleteAccountModal({ open: true, id });
  };
  const handleOpenDetailAccountModal = (data) => {
    setDetailAccountModal({ open: true, data });
  };

  const handleOpenCreateEditRecipientModal = useCallback(
    (recipientId) => {
      setCreateEditRecipientModal({ open: true, id, recipientId });
    },
    [id],
  );
  const handleOpenDeleteRecipientModal = useCallback(
    (recipientId) => {
      setDeleteRecipientModal({ open: true, id, recipientId });
    },
    [id],
  );

  const bodyCells = useMemo(
    () =>
      accountList?.data?.company_users?.map((el) => ({
        ...el,
        role: SHOWN_USER_ROLE?.[el?.role] || capitalize(el?.role),
        name: <OverflowText text={el?.name} />,
        status: (
          <div
            className={`rounded-full px-2 py-1 ${
              el?.status === "ACTIVE"
                ? "bg-[#CAFAD2] text-[#039E5A] max-w-[60px]"
                : "bg-[#FDDCD3] text-[#E52A49] max-w-[70px]"
            }`}
          >
            {capitalize(el?.status)}
          </div>
        ),
        actions: (
          <div className='flex align-middle gap-2'>
            <RemoveRedEyeOutlinedIcon
              className='cursor-pointer'
              onClick={() => handleOpenDetailAccountModal(el)}
              sx={{ color: "#146BDE" }}
            />
            {isAdminView && (
              <>
                <EditOutlinedIcon
                  className='cursor-pointer'
                  onClick={() => handleOpenCreateEditAccountModal(el?.id)}
                  sx={{ color: "#FBD603" }}
                />
                <DeleteOutlinedIcon
                  className='cursor-pointer'
                  onClick={() => handleOpenDeleteAccountModal(el?.id)}
                  sx={{ color: "#E52A49" }}
                />
              </>
            )}
          </div>
        ),
      })),
    [accountList, isAdminView],
  );

  const recipientsHeadCells = useMemo(
    () => [
      {
        id: "email",
        label: <p className='text-xs'>Account E-mail</p>,
        width: isAdminView ? "90%" : "100%",
      },
      ...(isAdminView
        ? [
            {
              id: "actions",
              label: <p className='text-xs'>Actions</p>,
              width: "10%",
            },
          ]
        : []),
    ],
    [isAdminView],
  );

  const recipientsBodyCells = useMemo(() => {
    let emailRecipients = [
      ...(companyDetail?.data?.email_recipients || []),
    ]?.map((el, idx) => ({ email: el, id: idx }));

    // handle manual search email
    if (queryParams?.["monthly-report-recipients-table_email"]) {
      emailRecipients =
        [...emailRecipients]?.filter((el) =>
          el?.email
            ?.toLowerCase()
            ?.includes(
              queryParams?.[
                "monthly-report-recipients-table_email"
              ]?.toLowerCase(),
            ),
        ) || [];
    }

    return emailRecipients?.map((el) => ({
      email: <OverflowText text={el?.email} maxChar={140} />,
      actions: (
        <div className='flex align-middle gap-2'>
          {isAdminView && (
            <>
              <EditOutlinedIcon
                className='cursor-pointer'
                onClick={() => handleOpenCreateEditRecipientModal(el?.id)}
                sx={{ color: "#FBD603" }}
              />
              {(companyDetail?.data?.email_recipients || [])?.length > 1 && (
                <DeleteOutlinedIcon
                  className='cursor-pointer'
                  onClick={() => handleOpenDeleteRecipientModal(el?.id)}
                  sx={{ color: "#E52A49" }}
                />
              )}
            </>
          )}
        </div>
      ),
    }));
  }, [
    companyDetail?.data?.email_recipients,
    handleOpenCreateEditRecipientModal,
    handleOpenDeleteRecipientModal,
    isAdminView,
    queryParams,
  ]);

  const handleCloseCreateEditAccountModal = useCallback(() => {
    setCreateEditAccountModal({ open: false });
    refetchAccountList();
    refetchAccountListWithoutLimit();
  }, [refetchAccountList, refetchAccountListWithoutLimit]);
  const handleCloseCreateEditClientModal = useCallback(() => {
    setCreateEditClientModal({ open: false });
    refetchCompanyDetail();
  }, [refetchCompanyDetail]);
  const handleCloseDeleteAccountModal = useCallback(() => {
    setDeleteAccountModal({ open: false });
    refetchAccountList();
    refetchAccountListWithoutLimit();
  }, [refetchAccountList, refetchAccountListWithoutLimit]);
  const handleCloseDetailAccountModal = () => {
    setDetailAccountModal({ open: false });
  };
  const handleCloseCreateEditRecipientModal = () => {
    setCreateEditRecipientModal({ open: false });
    refetchCompanyDetail();
  };
  const handleCloseDeleteRecipientModal = () => {
    setDeleteRecipientModal({ open: false });
    refetchCompanyDetail();
  };

  // change layout title
  useEffect(() => {
    setLayoutTitle(
      <div className='flex flex-row gap-2 items-center'>
        <p
          onClick={() => navigate("/client-management")}
          className='text-[#5F737F80] font-bold cursor-pointer'
        >
          Client Management
        </p>
        <KeyboardArrowRightOutlinedIcon />
        <p className='font-bold cursor-pointer'>
          {companyDetail?.data?.name ?? "Client Detail"}
        </p>
      </div>,
    );
    return () => {
      setLayoutTitle();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyDetail]);

  return (
    <div className='client-management-page-detail h-full'>
      {documentAccess.view && <CompanyDocumentOverview companyId={Number(id)} companyName={companyDetail?.data?.name} companyCode={companyDetail?.data?.effect_code} onReview={setSelectedDocument} />}
      {selectedDocument && <DocumentReviewModal documentId={selectedDocument} onClose={()=>setSelectedDocument(null)} onSaved={()=>{}} />}
      {/* Detail Company */}
      <div className='flex flex-row section h-full mb-10 gap-10 justify-between'>
        {companyDetail?.data?.logo_url ? (
          <div className='rounded-full h-[100px] w-100px'>
            <img
              className='rounded-full object-cover h-full'
              alt='company-logo'
              width={100}
              src={companyDetail?.data?.logo_url}
            />
          </div>
        ) : (
          <AccountCircleRoundedIcon
            viewBox='2 2 20 20'
            aria-label='account-profile-image-default'
            className='p-0'
            sx={{ height: 100, width: 100, color: "#ccc" }}
          />
        )}
        <div className='w-[150px]'>
          <div className='mt-1 text-[#5F737F] text-sm'>Client Name</div>
          <div className='mt-4 text-[#5F737F] text-sm'>Listing Year</div>
          <div className='mt-4 text-[#5F737F] text-sm'>Category</div>
        </div>
        <div className='w-[340px]'>
          <div className='flex gap-2 align-middle'>
            <OverflowText
              maxChar={35}
              textClassName='mt-1 text-sm'
              text={companyDetail?.data?.name}
            />
            <div
              className={`rounded-lg px-2 py-1 ${
                companyDetail?.data?.subscription_type === "FREE"
                  ? "bg-[#94A1A9]"
                  : "bg-[#556FB5]"
              }`}
            >
              <p className='text-sm text-white font-bold'>
                {companyDetail?.data?.subscription_type}
              </p>
            </div>
          </div>
          <p className='mt-3 text-sm'>
            {companyDetail?.data?.year_established}
          </p>
          <p className='mt-4 text-sm'>{companyDetail?.data?.category_name}</p>
        </div>
        <div className='w-[100px]'>
          <div className='mt-10 text-[#5F737F] text-sm'>Sector</div>
          <div className='mt-4 text-[#5F737F] text-sm'>Industry</div>
        </div>
        <div className='w-[200px]'>
          <p className='mt-10 text-sm'>{companyDetail?.data?.sector_name}</p>
          <p className='mt-[18px] text-sm'>
            {companyDetail?.data?.industry_name}
          </p>
        </div>
        {isAdminView && (
          <div className='self-center'>
            <Button
              onClick={() => handleOpenCreateEditClientModal(id)}
              className='w-[200px] h-[40px]'
              variant='outlined'
            >
              <p className='text-sm font-extrabold text-black'>Edit Client</p>
            </Button>
          </div>
        )}
      </div>

      {/* Account List */}
      <p className='font-semibold mb-4'>Accounts</p>
      <div className='section'>
        <div className='flex flex-row mb-5 justify-between'>
          <FilterGroupComponent
            showStatusFilter={false}
            showTypeFilter={false}
            showDateRangePicker={false}
            searchPlaceholder='Search by Full Name'
            searchParamsQueryKey='client-management-list-table_name'
            uniquePageParams='client-management-list-table_page'
          />
          {isAdminView && (
            <Tooltip
              title={"Active account limit is reached"}
              disableHoverListener={showCreateAccountBtn}
            >
              <div
                className={
                  showCreateAccountBtn ? "cursor-auto" : "cursor-not-allowed"
                }
              >
                <Button
                  onClick={() => handleOpenCreateEditAccountModal()}
                  variant='contained'
                  className={
                    showCreateAccountBtn ? "!bg-[#556FB5]" : "!bg-gray-200"
                  }
                  disabled={!showCreateAccountBtn}
                >
                  Create New Account
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
        <CustomTable
          id='client-management-list-table'
          headCells={headCells}
          bodyCells={bodyCells ?? []}
          bodyCellKey='name'
          total={accountList?.data?.total}
          uniqueParams
        />
      </div>

      {createEditClientModal?.open && (
        <CreateEditClientModal
          modalProps={{
            open: createEditClientModal?.open,
            onClose: () => handleCloseCreateEditClientModal(),
          }}
          id={createEditClientModal?.id}
          onClose={handleCloseCreateEditClientModal}
          setMessage={setMessage}
        />
      )}

      {createEditAccountModal?.open && (
        <CreateEditAccountModal
          modalProps={{
            open: createEditAccountModal?.open,
            onClose: () => handleCloseCreateEditAccountModal(),
          }}
          id={createEditAccountModal?.id}
          onClose={handleCloseCreateEditAccountModal}
        />
      )}

      {detailAccountModal?.open && (
        <DetailAccountModal
          modalProps={{
            open: detailAccountModal?.open,
            onClose: () => handleCloseDetailAccountModal(),
          }}
          data={detailAccountModal?.data}
          onClose={handleCloseDetailAccountModal}
        />
      )}

      {deleteAccountModal?.open && (
        <DeleteAccountModal
          modalProps={{
            open: deleteAccountModal?.open,
            onClose: () => handleCloseDeleteAccountModal(),
          }}
          id={deleteAccountModal?.id}
          onClose={handleCloseDeleteAccountModal}
        />
      )}

      {/* Email Recipients */}
      <p className='font-semibold mt-10 mb-4'>Monthly Report Recipient</p>
      <div className='section'>
        <div className='flex flex-row mb-5 justify-between'>
          <FilterGroupComponent
            showStatusFilter={false}
            showTypeFilter={false}
            showDateRangePicker={false}
            searchPlaceholder='Search by email'
            searchParamsQueryKey='monthly-report-recipients-table_email'
            uniquePageParams='monthly-report-recipients-table_page'
          />
          {isAdminView && (
            <Tooltip
              title={"Recipients limit is reached"}
              disableHoverListener={showCreateRecipientBtn}
            >
              <div
                className={
                  showCreateRecipientBtn ? "cursor-auto" : "cursor-not-allowed"
                }
              >
                <Button
                  onClick={() => handleOpenCreateEditRecipientModal()}
                  variant='contained'
                  className={
                    showCreateRecipientBtn ? "!bg-[#556FB5]" : "!bg-gray-200"
                  }
                  disabled={!showCreateRecipientBtn}
                >
                  Add New Recipient
                </Button>
              </div>
            </Tooltip>
          )}
        </div>
        <CustomTable
          id='monthly-report-recipients-table'
          headCells={recipientsHeadCells}
          bodyCells={recipientsBodyCells ?? []}
          bodyCellKey='email'
          total={(recipientsBodyCells ?? [])?.length ?? 0}
          showPagination={false}
          uniqueParams
        />
      </div>

      {createEditRecipientModal?.open && (
        <CreateEditRecipientModal
          modalProps={{
            open: createEditRecipientModal?.open,
            onClose: () => handleCloseCreateEditRecipientModal(),
          }}
          id={createEditRecipientModal?.id}
          recipientId={createEditRecipientModal?.recipientId}
          onClose={handleCloseCreateEditRecipientModal}
          setMessage={setMessage}
        />
      )}

      {deleteRecipientModal?.open && (
        <DeleteRecipientModal
          modalProps={{
            open: deleteRecipientModal?.open,
            onClose: () => handleCloseDeleteRecipientModal(),
          }}
          id={deleteRecipientModal?.id}
          recipientId={deleteRecipientModal?.recipientId}
          onClose={handleCloseDeleteRecipientModal}
        />
      )}

      {message.desc && (
        <SnackbarComponent
          labelTitle={message?.title}
          labelMessage={message?.desc}
          type={message?.type}
          onClose={() => setMessage(emptyToastMessage)}
          duration={5000}
        />
      )}
    </div>
  );
};

export default ClientManagementDetailPage;
